import { useCookie } from "#imports";
import { useVoixBroadcast } from "@voix/composables/useVoixBroadcast";
import { defineStore } from "pinia";
import queryString from "query-string";
import { v4 as uuidv4 } from "uuid";
import { computed, reactive, ref } from "vue";
import { usePageStore } from "./pageStore.js";
export const useAdminStore = defineStore("adminStore", () => {
  const broadcast = useVoixBroadcast();
  const currentAdminId = uuidv4();
  const isZenMode = ref(false);
  const isLoading = ref(false);
  const currentScale = ref(1);
  const currentDomain = ref(null);
  const currentSection = ref(null);
  const focusMode = ref(false);
  const releaseId = ref(null);
  const scrollToElement = ref({});
  const highlightElement = ref({});
  const selectedElements = ref([]);
  const selectedField = ref({});
  const selectedGroup = ref({});
  const selectedFieldSlice = ref({});
  const currentlySelectedTemplatizerSlice = reactive({ label: "", group: "" });
  const isDragging = ref(false);
  const makingRecipeElement = ref(null);
  const currentContextItem = reactive({ element: null, x: 0, y: 0 });
  const themeColor = ref("#4338ca");
  const voixAdminThemeColorCookie = useCookie("voix-admin-color");
  function setThemeColor(color) {
    voixAdminThemeColorCookie.value = color;
    themeColor.value = color;
  }
  if (voixAdminThemeColorCookie.value)
    themeColor.value = voixAdminThemeColorCookie.value;
  const selectedFieldData = computed(() => {
    if (selectedFieldSlice.value && selectedField.value.type) {
      if (!selectedFieldSlice.value.fields)
        selectedFieldSlice.value.fields = [];
      const foundField = selectedFieldSlice.value.fields?.find(
        (f) => f.name === selectedField.value.name
      );
      return foundField;
    }
    return null;
  });
  function setReleaseId(rid) {
    const releasePreviewCookie = useCookie("voix-release-preview");
    releasePreviewCookie.value = rid;
    releaseId.value = rid;
  }
  function unsetReleaseId() {
    const releasePreviewCookie = useCookie("voix-release-preview");
    releasePreviewCookie.value = null;
    releaseId.value = null;
  }
  function setSelectedElement(element) {
    selectedField.value = {};
    selectedElements.value = [element];
  }
  function unselectField() {
    selectedField.value = {};
    selectedFieldSlice.value = {};
  }
  function unselectGroup() {
    selectedGroup.value = {};
    selectedFieldSlice.value = {};
  }
  function unsetSelectedElement() {
    selectedField.value = {};
    selectedElements.value = [];
    unselectField();
  }
  function addToSelectedElements(element) {
    selectedElements.value.push(element);
  }
  function removeFromSelectedElements(element) {
    selectedElements.value.splice(selectedElements.value.indexOf(element), 1);
  }
  function unselectElements() {
    selectedElements.value = [];
    unselectField();
  }
  function unselectAll() {
    unselectGroup();
    unselectElements();
  }
  function setHighlightedElement(element) {
    highlightElement.value = element;
  }
  function unsetHighlightedElement() {
    highlightElement.value = {};
  }
  function selectField(slice, fieldConfig) {
    unselectField();
    unselectGroup();
    setupSliceFields(slice, fieldConfig);
    selectedFieldSlice.value = slice;
    selectedField.value = fieldConfig;
  }
  function selectGroup(slice, group) {
    unselectField();
    unselectGroup();
    Object.keys(group).forEach((key) => {
      setupSliceFields(slice, group[key]);
    });
    selectedFieldSlice.value = slice;
    selectedGroup.value = group;
  }
  function toggleSelectField(slice, fieldConfig) {
    if (fieldConfig.name === selectedField.value.name)
      unselectField();
    else
      selectField(slice, fieldConfig);
  }
  function toggleSelectGroup(slice, group) {
    const currentGroupKeys = Object.keys(selectedGroup.value);
    let isSameGroup = true;
    if (currentGroupKeys.length) {
      if (currentGroupKeys.length !== Object.keys(group).length) {
        isSameGroup = false;
      } else {
        currentGroupKeys.forEach((key) => {
          if (!group[key])
            isSameGroup = false;
        });
      }
      unselectGroup();
    } else {
      isSameGroup = false;
    }
    if (!isSameGroup)
      selectGroup(slice, group);
  }
  function createNewField(fieldConfig) {
    const newField = {
      id: uuidv4(),
      name: fieldConfig.name,
      type: fieldConfig.type,
      enabled: Object.prototype.hasOwnProperty.call(fieldConfig, "enabled") ? fieldConfig.enabled : true,
      properties: []
    };
    newField[fieldConfig.type] = fieldConfig.default;
    return newField;
  }
  function setupSliceFields(slice, fieldConfig, shouldBroadcast = true) {
    if (slice?.id) {
      const pageStore = usePageStore();
      const sliceData = pageStore.findElement(slice.id)?.element;
      if (Object.keys(fieldConfig).length && sliceData && sliceData.fields) {
        const foundField = sliceData.fields?.find(
          (f) => f.name === fieldConfig.name
        );
        if (!foundField) {
          const newField = createNewField(fieldConfig);
          if (!sliceData.fields)
            sliceData.fields = [newField];
          else
            sliceData.fields.push(newField);
          if (shouldBroadcast)
            pageStore.shareBroadcast("setupSliceFields", { slice, fieldConfig });
        }
      }
    }
  }
  function makeRecipeFromPage() {
    const pageStore = usePageStore();
    makingRecipeElement.value = pageStore.currentPage.elements;
  }
  function makeRecipeFromElement(element) {
    makingRecipeElement.value = [element];
  }
  function setCurrentContextItem(element, x, y, shouldBroadcast = true) {
    const pageStore = usePageStore();
    currentContextItem.element = element;
    currentContextItem.x = x;
    currentContextItem.y = y;
    if (shouldBroadcast)
      pageStore.shareBroadcast("setCurrentContextItem", { element, x, y });
  }
  if (typeof window !== "undefined") {
    const urlParams = queryString.parse(window.location.search);
    if (urlParams["voix-sync"] !== void 0) {
      broadcast.receive(`setupSliceFields:${urlParams["voix-sync"]}`, (data) => {
        console.log("setupSliceFields");
        setupSliceFields(data.slice, data.fieldConfig, false);
      });
    } else {
      broadcast.receive(`setCurrentContextItem:${currentAdminId}`, (data) => {
        console.log("setCurrentContextItem");
        data.x += 400;
        data.y += 130;
        setCurrentContextItem(data.element, data.x, data.y, false);
      });
    }
  }
  return {
    // State
    currentAdminId,
    isZenMode,
    isLoading,
    currentScale,
    currentDomain,
    currentSection,
    focusMode,
    releaseId,
    scrollToElement,
    highlightElement,
    selectedElements,
    selectedField,
    selectedGroup,
    selectedFieldSlice,
    themeColor,
    isDragging,
    currentlySelectedTemplatizerSlice,
    makingRecipeElement,
    currentContextItem,
    // Getters
    selectedFieldData,
    // Actions
    setThemeColor,
    setReleaseId,
    unsetReleaseId,
    setSelectedElement,
    unsetSelectedElement,
    addToSelectedElements,
    removeFromSelectedElements,
    unselectElements,
    setHighlightedElement,
    unsetHighlightedElement,
    createNewField,
    selectField,
    selectGroup,
    unselectField,
    unselectGroup,
    unselectAll,
    toggleSelectField,
    toggleSelectGroup,
    setupSliceFields,
    makeRecipeFromElement,
    makeRecipeFromPage,
    setCurrentContextItem
  };
});
